/**
 * Bank Account Management panel.
 *
 * Lists every bank account (including hidden ones) so the operator can
 * archive/un-archive individual accounts. Also provides a "Retire Bridge
 * Accounts" bulk action with a two-step preview/apply flow.
 *
 * Reuses the existing hidden-flag archive mechanism: setting hidden=true
 * removes the account from Bank Balances and dashboard summaries.
 */

import { useState, useCallback } from 'react';
import {
  Loader2, Archive, ArchiveRestore, Search, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { bankAccountsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatBalance(balance, currency) {
  if (balance == null) return '--';
  return Number(balance).toLocaleString('en-GB', {
    style: 'currency',
    currency: currency || 'GBP',
    minimumFractionDigits: 2,
  });
}

function formatAsOf(isoStr) {
  if (!isoStr) return 'Never';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

function SourceBadge({ source }) {
  var colorClass = 'text-muted-foreground';
  if (source === 'bridge') colorClass = 'text-amber-700 bg-amber-50 border-amber-200';
  if (source === 'mock') colorClass = 'text-red-700 bg-red-50 border-red-200';
  if (source === 'openbanking') colorClass = 'text-blue-700 bg-blue-50 border-blue-200';
  if (source === 'revolut') colorClass = 'text-indigo-700 bg-indigo-50 border-indigo-200';
  if (source === 'wise') colorClass = 'text-green-700 bg-green-50 border-green-200';

  return (
    <Badge variant="outline" className={'text-[10px] px-1.5 py-0 ' + colorClass}>
      {source || 'unknown'}
    </Badge>
  );
}

function HiddenBadge({ hidden }) {
  if (!hidden) return null;
  return (
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 bg-red-50 border-red-200">
      archived
    </Badge>
  );
}

export function BankAccountManagementPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);
  var [actionLoading, setActionLoading] = useState(null); // account_id being acted on
  var [bridgePreview, setBridgePreview] = useState(null);
  var [bridgePreviewLoading, setBridgePreviewLoading] = useState(false);
  var [bridgeApplyLoading, setBridgeApplyLoading] = useState(false);
  var [showHidden, setShowHidden] = useState(true);

  var loadAccounts = useCallback(async function () {
    setLoading(true);
    setData(null);
    setBridgePreview(null);
    try {
      var res = await bankAccountsAPI.managementList();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  var handleArchive = useCallback(async function (accountId) {
    setActionLoading(accountId);
    try {
      await bankAccountsAPI.archiveAccount(accountId);
      toast.success('Archived account');
      await loadAccounts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionLoading(null);
    }
  }, [loadAccounts]);

  var handleUnarchive = useCallback(async function (accountId) {
    setActionLoading(accountId);
    try {
      await bankAccountsAPI.unarchiveAccount(accountId);
      toast.success('Un-archived account');
      await loadAccounts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActionLoading(null);
    }
  }, [loadAccounts]);

  var loadBridgePreview = useCallback(async function () {
    setBridgePreviewLoading(true);
    setBridgePreview(null);
    try {
      var res = await bankAccountsAPI.bridgePreview();
      setBridgePreview(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBridgePreviewLoading(false);
    }
  }, []);

  var handleBridgeApply = useCallback(async function () {
    setBridgeApplyLoading(true);
    try {
      var res = await bankAccountsAPI.archiveBridge();
      var result = res.data;
      toast.success(
        'Archived ' + result.modified + ' Bridge account' +
        (result.modified !== 1 ? 's' : '')
      );
      setBridgePreview(null);
      await loadAccounts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBridgeApplyLoading(false);
    }
  }, [loadAccounts]);

  var accounts = data ? data.accounts : [];
  var visibleAccounts = showHidden
    ? accounts
    : accounts.filter(function (a) { return !a.hidden; });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bank Account Management</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Lists every bank account (including archived). Archive sets hidden=true,
          removing the account from Bank Balances. Reversible. Use "Retire Bridge
          Accounts" to bulk-archive all Bridge-sourced accounts (retired provider).
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={loadAccounts} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Load Accounts
          </Button>

          {data && (
            <Button
              variant="outline"
              onClick={loadBridgePreview}
              disabled={bridgePreviewLoading}
            >
              {bridgePreviewLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Retire Bridge Accounts
            </Button>
          )}
        </div>

        {/* ── Bridge preview/apply ────────────────────────────────── */}
        {bridgePreview && (
          <div className="border rounded-lg p-3 space-y-3 bg-amber-50/50">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold">
                Bridge Accounts Preview ({bridgePreview.count} account{bridgePreview.count !== 1 ? 's' : ''})
              </span>
            </div>

            {bridgePreview.count === 0 ? (
              <p className="text-sm text-muted-foreground">No Bridge-sourced accounts found.</p>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">
                  {bridgePreview.would_archive} to archive, {bridgePreview.already_hidden} already archived
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1.5 pr-3 font-medium">Account</th>
                        <th className="py-1.5 pr-3 font-medium">Entity</th>
                        <th className="py-1.5 pr-3 font-medium">Source</th>
                        <th className="py-1.5 pr-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bridgePreview.accounts.map(function (acc) {
                        return (
                          <tr key={acc.id} className="border-b border-border/50">
                            <td className="py-1.5 pr-3">{acc.account_name}</td>
                            <td className="py-1.5 pr-3 text-xs">{acc.entity_name}</td>
                            <td className="py-1.5 pr-3">
                              <SourceBadge source={acc.source} />
                            </td>
                            <td className="py-1.5 pr-3">
                              {acc.hidden ? (
                                <span className="text-xs text-muted-foreground">already archived</span>
                              ) : (
                                <span className="text-xs text-amber-700">will archive</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {bridgePreview.would_archive > 0 && (
                  <Button
                    variant="default"
                    onClick={handleBridgeApply}
                    disabled={bridgeApplyLoading}
                  >
                    {bridgeApplyLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Archive className="h-4 w-4 mr-2" />
                    )}
                    Apply: Archive {bridgePreview.would_archive} Bridge Account{bridgePreview.would_archive !== 1 ? 's' : ''}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Account listing ─────────────────────────────────────── */}
        {data && (
          <div className="space-y-3">
            {/* Summary row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <span>{data.total} total</span>
                <span className="text-muted-foreground">{data.visible_count} visible</span>
                {data.hidden_count > 0 && (
                  <span className="text-red-600">{data.hidden_count} archived</span>
                )}
              </div>
              {data.hidden_count > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={function () { setShowHidden(!showHidden); }}
                >
                  {showHidden ? (
                    <>
                      <ChevronUp className="h-3 w-3 mr-1" />
                      Hide archived
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Show archived ({data.hidden_count})
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Account table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Account</th>
                    <th className="py-1.5 pr-3 font-medium">Entity</th>
                    <th className="py-1.5 pr-3 font-medium">Bank</th>
                    <th className="py-1.5 pr-3 font-medium">Source</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Balance</th>
                    <th className="py-1.5 pr-3 font-medium">Last Synced</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                    <th className="py-1.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAccounts.map(function (acc) {
                    var isActing = actionLoading === acc.id;
                    return (
                      <tr
                        key={acc.id}
                        className={
                          'border-b border-border/50' +
                          (acc.hidden ? ' opacity-60 bg-muted/30' : '')
                        }
                      >
                        <td className="py-1.5 pr-3">{acc.account_name}</td>
                        <td className="py-1.5 pr-3 text-xs">{acc.entity_name}</td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{acc.bank_name}</td>
                        <td className="py-1.5 pr-3">
                          <SourceBadge source={acc.source} />
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono text-xs">
                          {formatBalance(acc.balance, acc.currency)}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                          {formatAsOf(acc.last_synced)}
                        </td>
                        <td className="py-1.5 pr-3">
                          <HiddenBadge hidden={acc.hidden} />
                        </td>
                        <td className="py-1.5">
                          {acc.hidden ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={function () { handleUnarchive(acc.id); }}
                              disabled={isActing || actionLoading != null}
                              className="text-xs h-6 px-2"
                            >
                              {isActing ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <ArchiveRestore className="h-3 w-3 mr-1" />
                              )}
                              Un-archive
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={function () { handleArchive(acc.id); }}
                              disabled={isActing || actionLoading != null}
                              className="text-xs h-6 px-2"
                            >
                              {isActing ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <Archive className="h-3 w-3 mr-1" />
                              )}
                              Archive
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {visibleAccounts.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No accounts to display.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
